import * as React from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, Info, CheckCircle2, ArrowRight } from "lucide-react";
import { cn } from "../cn";
import { formatRelativeShort } from "../format";

// An itemised analyst briefing: a small set of decision-oriented alerts, each a severity
// icon + one-line statement + a single action link. Replaces wall-of-prose briefings.
// Attributed to the analyst (Heimdall) so the "every signal has an owner" principle shows.
export type BriefingSeverity = "serious" | "attention" | "info";

const SEVERITY_META: Record<BriefingSeverity, { icon: React.ElementType; fg: string }> = {
  serious: { icon: AlertOctagon, fg: "text-bad" },
  attention: { icon: AlertTriangle, fg: "text-warn" },
  info: { icon: Info, fg: "text-info" },
};

export type BriefingAlert = {
  id: string;
  severity: BriefingSeverity;
  /** One-line statement, plain language. */
  title: string;
  /** Optional supporting detail. */
  detail?: string;
  /** Single action link. onClick, when given alongside href, fires before
   * the Link navigates (doesn't prevent it) — e.g. switching the active
   * workspace so href lands in the right one. onClick alone (no href) is
   * still supported for a plain in-page action. */
  action?: { label: string; href?: string; onClick?: () => void };
  /** The workspace this alert concerns, when the caller has resolved one —
   * purely data (see AttentionItem.workspaceId's identical note). A caller
   * that knows how to switch workspaces derives action.onClick from this. */
  workspaceId?: string;
};

function AlertAction({ action }: { action: NonNullable<BriefingAlert["action"]> }) {
  const cls = "inline-flex items-center gap-0.5 text-xs font-medium text-brand hover:underline";
  if (action.href) {
    return (
      <Link to={action.href} onClick={action.onClick} className={cls}>
        {action.label} <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }
  return (
    <button type="button" onClick={action.onClick} className={cls}>
      {action.label} <ArrowRight className="h-3 w-3" />
    </button>
  );
}

export function Briefing({
  alerts,
  analystName = "Heimdall",
  updatedAt,
  calmLabel = "Nothing needs your attention right now.",
  className,
}: {
  alerts: BriefingAlert[];
  analystName?: string;
  /** ISO timestamp for the "updated" footer. */
  updatedAt?: string;
  calmLabel?: string;
  className?: string;
}) {
  return (
    <div className={cn("rounded-card border border-line bg-card p-3", className)}>
      {alerts.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-mute">
          <CheckCircle2 className="h-4 w-4 text-ok" aria-hidden />
          {calmLabel}
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {alerts.map((a) => {
            const m = SEVERITY_META[a.severity];
            const Icon = m.icon;
            return (
              <li key={a.id} className="flex items-start gap-2.5">
                <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", m.fg)} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium leading-snug text-ink">{a.title}</p>
                  {a.detail && <p className="mt-0.5 text-xs leading-snug text-mute">{a.detail}</p>}
                  {a.action && (
                    <div className="mt-1">
                      <AlertAction action={a.action} />
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {/* TODO(heimdall): AI analyst not built yet — re-enable the "{analyst} summary" footer when Heimdall ships.
      <p className="mt-3 border-t border-line pt-2 text-[11px] text-faint">
        {analystName} summary{updatedAt ? ` · updated ${formatRelativeShort(updatedAt)}` : ""}
      </p>
      */}
    </div>
  );
}
